import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { FileText, PaperPlaneTilt } from '@phosphor-icons/react';
import { formatCurrency, formatDateTime, formatBytes } from './constants';

export const ClaimsTable = ({ claims }) => {
  const navigate = useNavigate();
  return (
    <Card data-testid="odm-detail-claims-table">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileText size={18} className="text-indigo-600" />
          Claims in this batch
        </CardTitle>
        <CardDescription className="text-xs">
          {claims.length} claim{claims.length === 1 ? '' : 's'} sent — click any row to open the claim
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600 tracking-wider">
              <tr>
                <th className="px-3 py-2">Claim #</th>
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Service date</th>
                <th className="px-3 py-2 text-right">Charge</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">999 Ack</th>
                <th className="px-3 py-2">277CA</th>
                <th className="px-3 py-2 text-right">Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {claims.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                    No claim records linked to this batch.
                  </td>
                </tr>
              )}
              {claims.map((c) => (
                <tr
                  key={c.id}
                  onClick={() =>
                    navigate(`/claims?claim=${encodeURIComponent(c.id)}`)
                  }
                  className="hover:bg-indigo-50/30 cursor-pointer"
                  data-testid={`odm-detail-claim-row-${c.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{c.claim_number || '—'}</td>
                  <td className="px-3 py-2 truncate max-w-[180px]">{c.patient_name}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                    {c.service_date || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCurrency(c.charge_amount)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                      {c.status || '—'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    {c.ack_status ? (
                      <Badge
                        className={`text-[10px] ${
                          c.ack_status === 'Accepted'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {c.ack_status}
                      </Badge>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-700">
                    {c.claim_277_status || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">
                    {c.payment_amount ? formatCurrency(c.payment_amount) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const SkippedList = ({ batch }) => {
  if (!batch.skipped_count) return null;
  return (
    <div className="col-span-2 md:col-span-4">
      <p className="text-xs text-slate-500 mb-1">
        Skipped at submit ({batch.skipped_count})
      </p>
      <ul className="text-xs text-slate-600 space-y-1">
        {(batch.skipped || []).map((s, i) => (
          <li key={`${s.claim_id || i}`} className="font-mono">
            {s.claim_number || s.claim_id}: {s.reason}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const BatchMetadataCard = ({ batch }) => (
  <Card data-testid="odm-detail-meta-card">
    <CardHeader>
      <CardTitle className="text-sm flex items-center gap-2">
        <PaperPlaneTilt size={16} className="text-slate-500" />
        Batch Metadata
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-xs text-slate-500">Submitted at</p>
          <p className="font-medium text-slate-800">{formatDateTime(batch.submitted_at)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">ISA Control #</p>
          <p className="font-mono text-slate-800">{batch.isa_control_number || '—'}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">File size</p>
          <p className="font-medium text-slate-800">{formatBytes(batch.file_size)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500">Transaction type</p>
          <p className="font-medium text-slate-800">{batch.transaction_type || '837P'}</p>
        </div>
        <SkippedList batch={batch} />
      </div>
    </CardContent>
  </Card>
);
